import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Button,
  Divider,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Settings as SettingsIcon,
  ReceiptLong as ReceiptLongIcon,
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../context/ThemeContext';
import settingsService from '../services/settingsService';
import { Alert, Snackbar, CircularProgress, Backdrop } from '@mui/material';

function Settings() {
  const { mode, toggleTheme } = useThemeContext();
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    general: {
      autoSave: true,
      darkMode: false,
      printRemark: true,
      defaultB2Cprintsize: 'thermal',
      autoGenerate: false,
      customerAutoCode: false,
      itemAutoCode: false,
      supplierAutoCode: false
    },
    product: {
      defaultUnit: 'pcs',
      defaultTaxType: 'standard',
      enableBarcodeScanning: true,
      trackInventory: true
    },
    sales: {
      invoicePrefix: 'INV-',
      draftPrefix: 'DRF-',
      documentDiscountEnabled: true
    },
    purchase: {
      purchaseReturnPrefix: 'PR-',
      purchasePrefix: 'PO-',
      requirePurchaseApproval: true
    },
    template: {
      poTemplate: 'standard',
      salesInvoiceTemplate: 'standard',
      purchaseInvoiceTemplate: 'standard',
      creditNoteTemplate: 'standard',
      debitNoteTemplate: 'standard',
      purchaseReturnTemplate: 'standard',
      proformaInvoiceTemplate: 'standard',
      deliveryNoteTemplate: 'standard',
    }
  });

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getSettings();
      if (response.success && response.data) {
        setSettings(response.data);
        // Sync dark mode with theme context
        if (response.data.general?.darkMode !== (mode === 'dark')) {
          toggleTheme();
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleToggle = (category, field) => async (event) => {
    const newValue = event.target.checked;
    if (category === 'general' && field === 'darkMode') {
      toggleTheme();
    }

    handleCategoryChange(category, field, newValue);

    // Immediately trigger API call for specific general settings
    if (category === 'general' && ['printRemark', 'autoGenerate', 'customerAutoCode', 'itemAutoCode', 'supplierAutoCode'].includes(field)) {
      handleImmediateSave(category, field, newValue);
    }
  };

  const handleImmediateSave = async (category, field, value) => {
    try {
      setSaving(true);
      const updatedSettings = {
        ...settings,
        [category]: {
          ...settings[category],
          [field]: value,
          ...(field === 'printRemark' && { showPrintRemark: value })
        }
      };
      const response = await settingsService.updateSettings(updatedSettings);
      if (response.success) {
        if (response.data) {
          setSettings(response.data);
        }
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error(`Error saving ${field}:`, err);
      setError(err.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };



  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await settingsService.updateSettings(settings);

      if (response.success) {
        if (response.data) {
          setSettings(response.data);
        }
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Manage your application settings and preferences
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="small"
          sx={{
            borderRadius: 0,
            textTransform: 'none',
            py: 0.5,
            px: 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(event, newValue) => setActiveTab(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label="General" />
        <Tab label="Product" />
        <Tab label="Sales" />
        <Tab label="Purchase" />
        <Tab label="Template" />
      </Tabs>

      {/* Content Area */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 0.5,
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >

        {/* General Tab */}
        {activeTab === 0 && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.autoSave || false}
                      onChange={handleToggle('general', 'autoSave')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Auto Save
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Automatically save changes to your settings
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mode === 'dark'}
                      onChange={handleToggle('general', 'darkMode')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Dark Mode
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Switch between light and dark theme
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.printRemark ?? true}
                      onChange={handleToggle('general', 'printRemark')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Show Print Remark
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Include the remark field in print templates
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.autoGenerate || false}
                      onChange={handleToggle('general', 'autoGenerate')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Auto Generate
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Enable or disable the auto-generate feature
                      </Typography>
                    </Box>
                  }
                />
              </Grid> */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.customerAutoCode || false}
                      onChange={handleToggle('general', 'customerAutoCode')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Customer Auto Code
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Automatically generate codes for new customers
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.itemAutoCode || false}
                      onChange={handleToggle('general', 'itemAutoCode')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Item Auto Code
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Automatically generate codes for new items
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.general?.supplierAutoCode || false}
                      onChange={handleToggle('general', 'supplierAutoCode')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Supplier Auto Code
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Automatically generate codes for new suppliers
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" sx={{ width: '300px' }}>
                  <InputLabel>Default B2C Print Size</InputLabel>
                  <Select
                    value={settings.general?.defaultB2Cprintsize || 'thermal'}
                    onChange={(e) => {
                      handleCategoryChange('general', 'defaultB2Cprintsize', e.target.value);
                      handleImmediateSave('general', 'defaultB2Cprintsize', e.target.value);
                    }}
                    label="Default B2C Print Size"
                    sx={{ borderRadius: 0 }}
                  >
                    <MenuItem value="thermal">Thermal</MenuItem>
                    <MenuItem value="a4">A4 PDF</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Product Tab */}
        {activeTab === 1 && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Default Unit</InputLabel>
                  <Select
                    value={settings.product?.defaultUnit || 'pcs'}
                    onChange={(e) => handleCategoryChange('product', 'defaultUnit', e.target.value)}
                    label="Default Unit"
                    sx={{ borderRadius: 0 }}
                  >
                    <MenuItem value="pcs">Pieces (PCS)</MenuItem>
                    <MenuItem value="kg">Kilogram (KG)</MenuItem>
                    <MenuItem value="ltr">Liter (LTR)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Default Tax Type</InputLabel>
                  <Select
                    value={settings.product?.defaultTaxType || 'standard'}
                    onChange={(e) => handleCategoryChange('product', 'defaultTaxType', e.target.value)}
                    label="Default Tax Type"
                    sx={{ borderRadius: 0 }}
                  >
                    <MenuItem value="standard">Standard (15%)</MenuItem>
                    <MenuItem value="exempt">Exempt</MenuItem>
                    <MenuItem value="zero">Zero Rated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.product?.enableBarcodeScanning || false}
                      onChange={handleToggle('product', 'enableBarcodeScanning')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Enable Barcode Scanning
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Allow barcode scanning for product identification
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.product?.trackInventory || false}
                      onChange={handleToggle('product', 'trackInventory')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Track Inventory
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Enable inventory tracking for all products
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Sales Tab */}
        {activeTab === 2 && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Invoice Prefix"
                  value={settings.sales?.invoicePrefix || 'INV-'}
                  onChange={(e) => handleCategoryChange('sales', 'invoicePrefix', e.target.value)}
                  placeholder="e.g., INV-"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Draft Prefix"
                  value={settings.sales?.draftPrefix || 'DRF-'}
                  onChange={(e) => handleCategoryChange('sales', 'draftPrefix', e.target.value)}
                  placeholder="e.g., DRF-"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sales?.documentDiscountEnabled || false}
                      onChange={handleToggle('sales', 'documentDiscountEnabled')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Document Discount Enabled
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Allow applying discounts at the document level
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Purchase Tab */}
        {activeTab === 3 && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Purchase Return Prefix"
                  value={settings.purchase?.purchaseReturnPrefix || 'PR-'}
                  onChange={(e) => handleCategoryChange('purchase', 'purchaseReturnPrefix', e.target.value)}
                  placeholder="e.g., PR-"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Purchase Order Prefix"
                  value={settings.purchase?.purchasePrefix || 'PO-'}
                  onChange={(e) => handleCategoryChange('purchase', 'purchasePrefix', e.target.value)}
                  placeholder="e.g., PO-"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.purchase?.requirePurchaseApproval || false}
                      onChange={handleToggle('purchase', 'requirePurchaseApproval')}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        Require Purchase Approval
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Require approval before creating purchase orders
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Template Tab */}
        {activeTab === 4 && (
          <Box>
            <Grid container spacing={2}>
              {[
                { id: 'poTemplate', label: 'PO Template' },
                { id: 'salesInvoiceTemplate', label: 'Sales Invoice Template' },
                { id: 'purchaseInvoiceTemplate', label: 'Purchase Invoice Template' },
                { id: 'creditNoteTemplate', label: 'Credit Note Template' },
                { id: 'debitNoteTemplate', label: 'Debit Note Template' },
                { id: 'purchaseReturnTemplate', label: 'Purchase Return Template' },
                { id: 'proformaInvoiceTemplate', label: 'Proforma Invoice Template' },
                { id: 'deliveryNoteTemplate', label: 'Delivery Note Template' },
              ].map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{template.label}</InputLabel>
                    <Select
                      value={settings.template?.[template.id] || 'standard'}
                      onChange={(e) => handleCategoryChange('template', template.id, e.target.value)}
                      label={template.label}
                      sx={{ borderRadius: 0 }}
                    >
                      <MenuItem value="standard">Standard Template</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

      </Paper>
    </Container>
  );
}

export default Settings; 